import GroupLinkedDevice, { IGroupLinkedDevice } from "../model/GroupLinkedDevice";
import { GroupLinkedDeviceActions } from "../store/groupLinkedDevice/actions";
import * as common from "./common";
import BaseSync, { ISyncAddSet, NumberOfSyncRetries } from "./base";
import { IModifiedType } from "./sync.types";
import * as logger from "../utils/logger";
import { getServerDate } from "../utils/storage/date";
import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { extend, isEqual } from "../utils/vanilla";
import { getStartOfDayFrom } from "../utils/trip/tripHelper";
import { ISyncInterval } from "../store/mobile/types";
import { TMultiCallParam } from "../utils/api.interfaces";
import {
    isDuplicateException,
    isNetworkError,
    isOverLimitException,
    isRelationViolatedException
} from "../utils/errorHandler/error.types";
import { MeasureName } from "../utils/performanceProfiler/measureName";
import PerformanceProfileBatcher from "../utils/performanceProfiler/performanceProfileBatcher";
import { deviceNotInUserScopeException } from "../utils/errorHandler/error.messages";

const defaultGroupNamesById = {
    "GroupBusinessGroupId": "Business",
    "GroupPersonalGroupId": "Personal"
};

export default class GroupLinkedDeviceSync extends BaseSync {
    private syncInterval?: ISyncInterval;

    synchronize(progressCb: common.TSyncCallback, syncInterval?: ISyncInterval, profilingBatcher?: PerformanceProfileBatcher): Promise {
        return this.createProfiledSync(MeasureName.SyncGroupLinkedDevice, () => {
            this.syncInterval = syncInterval;

            // Last sync might have failed due to rate limiting. Don't repeat those calls
            if (this.didSyncFailRecently()) {
                logger.event(logger.Event.SYNCGROUPDEVICECONFLICT, "Skip GroupLinkedDevice sync because previous run failed very recently");
                progressCb("GroupLinkedDevice");
                return Promise.resolve();
            }

            let handleSyncSuccess = () => this.updateRunStateAtSyncComplete();
            let handleSyncError = error => this.updateRunStateAtSyncComplete(error);

            return new Promise(resolve => {
                this.fetchData()
                    .then(serverGroupLinkedDevices => {
                        const mergedGroupLinkedDevices = this.syncGroupLinkedDevices(serverGroupLinkedDevices);
                        return this.pushChangedToServer(mergedGroupLinkedDevices);
                    })
                    .then(syncedGroupLinkDevices => this.setGroupLinkedDevices(syncedGroupLinkDevices))
                    .then(() => {
                        progressCb("GroupLinkedDevice");
                        handleSyncSuccess();
                        resolve();
                    })
                    .catch(err => {
                        progressCb("GroupLinkedDevice", err);
                        resolve();
                        handleSyncError(err);
                    });
            });
        }, profilingBatcher);
    }

    fetchData(): Promise<IIdDictionary<IIdDictionary<GroupLinkedDevice>>> {
        /**
         * Fetch a list of devices the user has been associated with in the provided time range by looking in the Trips store
         */
        const getDeviceIds = () => {
            let trips = this.store.getState().trips;
            let uniqueIds: Set<string> = new Set();
            let userId = this.currentUser?.id;

            if (userId && trips.hasOwnProperty(userId)) {
                const tripStore = trips[userId];
                 Object.keys(tripStore).forEach(tripId => {
                    let device = tripStore[tripId].device;
                    if (device?.id && !uniqueIds.has(device.id)) {
                        uniqueIds.add(device.id);
                    }
                });
            }

            return Array.from(uniqueIds);
        };

        return new Promise((resolve, reject) => {
            // By default, fetch driver changes for the last two days. Later update to use stored interval in redux
            let syncInterval = this.syncInterval || this.store.getState().mobile.tripCategorizationSyncInterval;
            let fromDate = syncInterval?.fromDate || getStartOfDayFrom(2).toISOString();
            let toDate = syncInterval?.toDate || getServerDate().toISOString();

            const deviceIds = getDeviceIds();
            if (deviceIds.length === 0) {
                resolve({});
                return;
            }

            // Get<GroupDevice> for each device for the desired Date range
            const groupDeviceGetCalls: TMultiCallParam[] = deviceIds.map(deviceId => ["Get", { typeName: "GroupDevice", search: { deviceSearch: { id: deviceId }, fromDate, toDate }}]);

            this.api.multiCall(
                groupDeviceGetCalls,
                (results: IGroupLinkedDevice[][]) => {
                    const groupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>> = {};

                    deviceIds.forEach((deviceId, index) => {
                        const deviceDictionary = {};
                        results[index].forEach(groupDevice => {
                            // Only accept default "Personal" and "Business" groups right now
                            if (defaultGroupNamesById[groupDevice.group.id]) {
                                deviceDictionary[groupDevice.id] = new GroupLinkedDevice(
                                    {
                                        from: groupDevice.from,
                                        to: groupDevice.to,
                                        id: groupDevice.id,
                                        group: {id: groupDevice.group.id, name: defaultGroupNamesById[groupDevice.group.id]},
                                        linkedEntity: {id: groupDevice.linkedEntity.id}
                                    }
                                );
                            }
                        });
                        groupLinkedDevices[deviceId] = deviceDictionary;
                    });
                    resolve(groupLinkedDevices);
                },
                reject
            );
        });
    }

    /**
     * Merges GroupDevice records from the server with GroupDevice records that only exist locally.
     * @param serverGroupLinkedDevices The list of GroupDevice records from the server
     * @param localGroupLinkedDevices The list of GroupDevice records from memory
     */
    mergeGroupLinkedDevices(
        serverGroupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>,
        localGroupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>
    ): IIdDictionary<IIdDictionary<GroupLinkedDevice>> {
        logger.log(logger.Source.SYNC, "MergeGroupLinkedDevices");
        this.logGroupsTimeline("local", localGroupLinkedDevices);
        this.logGroupsTimeline("server", serverGroupLinkedDevices);

        Object.keys(serverGroupLinkedDevices).forEach(deviceId => {
            Object.keys(serverGroupLinkedDevices[deviceId]).forEach(serverGroupDeviceId => {
                let server = new GroupLinkedDevice(serverGroupLinkedDevices[deviceId][serverGroupDeviceId]);
                let local = localGroupLinkedDevices[deviceId]?.[serverGroupDeviceId];

                // Server log exists locally
                if (local) {
                    let previousState = local.previousState ? new GroupLinkedDevice(local.previousState) : undefined;
                    let modifiedState = local.modified;

                    if (modifiedState) {
                        if (!isEqual(previousState, server)) {
                            // Merge local and server if local is "Add"
                            if (modifiedState === "Add") {
                                logger.log(logger.Source.SYNC, `Merged local GroupLinkedDevice with server local: ${ JSON.stringify(local) } server: ${ JSON.stringify(server) }`);
                                localGroupLinkedDevices[deviceId][serverGroupDeviceId] = new GroupLinkedDevice(common.merge<GroupLinkedDevice>(local, server, { modified: "Set" }));
                            } else {
                                // Conflict if both server and local groupDevice are changed and local modification was "Edit", take server version
                                let localValue = JSON.stringify(local);
                                let serverValue = JSON.stringify(server);
                                logger.error(logger.Source.SYNC, `SYNC: GroupLinkedDevice conflict (server and client changed) Taking server log. loc: ${ localValue } ser: ${ serverValue }`);
                                localGroupLinkedDevices[deviceId][serverGroupDeviceId] = server;
                            }
                        }
                    } else {
                        // Server version is changed, take server version
                        if (!isEqual(local, server)) {
                            logger.log(logger.Source.SYNC, `Updated local GroupLinkedDevice from server local: ${ JSON.stringify(local) } server: ${ JSON.stringify(server) }`);
                            localGroupLinkedDevices[deviceId][serverGroupDeviceId] = server;
                        }
                        // Otherwise, local & server both not changed so do nothing.
                    }
                // If the server log doesn't exist locally, just add it to the list
                } else {
                    if (!localGroupLinkedDevices.hasOwnProperty(deviceId)) {
                        localGroupLinkedDevices[deviceId] = {};
                    }
                    localGroupLinkedDevices[deviceId][serverGroupDeviceId] = server;
                }
            });
        });

        return localGroupLinkedDevices;
    }

    /**
     * Fetch the GroupLinkedDevice dictionary from Redux storage
     */
    getGroupLinkedDevices(): IIdDictionary<IIdDictionary<GroupLinkedDevice>> {
        return this.convertToDictionary(extend(true, {}, this.store.getState().groupLinkedDevices));
    }

    private setGroupLinkedDevices(groupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>) {
        Object.keys(groupLinkedDevices).forEach(deviceId => {
            this.store.dispatch(GroupLinkedDeviceActions.setGroupLinkedDevices({ deviceId: deviceId, groupLinkedDevices: groupLinkedDevices[deviceId] }));
        });
    }

    private syncGroupLinkedDevices(serverGroupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>) {
        return this.mergeGroupLinkedDevices(serverGroupLinkedDevices || {}, this.getGroupLinkedDevices());
    }

    /**
     * Convert the DeepReadonlyObject version of the GroupDevice IIdDictionary into a regular, usable IIdDictionary
     * @param groupLinkedDevices The DeepReadonly version of the GroupDevice IIdDictionary (directly out of redux storage)
     */
    private convertToDictionary(groupLinkedDevices: DeepReadonlyObject<IIdDictionary<IIdDictionary<GroupLinkedDevice>>>): IIdDictionary<IIdDictionary<GroupLinkedDevice>> {
        let groupDeviceDictionary: IIdDictionary<IIdDictionary<GroupLinkedDevice>> = {};

        if (groupLinkedDevices) {
            Object.keys(groupLinkedDevices).forEach(deviceId => {
                if (deviceId) {
                    groupDeviceDictionary[deviceId] = groupLinkedDevices[deviceId];
                    Object.keys(groupDeviceDictionary[deviceId]).forEach(groupDeviceId => {
                        groupDeviceDictionary[deviceId][groupDeviceId] = new GroupLinkedDevice(groupDeviceDictionary[deviceId][groupDeviceId]);
                    });
                }
            });
        }
        return groupDeviceDictionary;
    }

    private logGroupsTimeline(source: string, groupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>) {
        Object.keys(groupLinkedDevices).forEach(deviceId => {
            const intervalsByGroup = {
                "GroupBusinessGroupId": [],
                "GroupPersonalGroupId": []
            };
            Object.keys(groupLinkedDevices[deviceId]).forEach(groupDeviceId => {
                const groupDevice = groupLinkedDevices[deviceId][groupDeviceId];
                if (intervalsByGroup.hasOwnProperty(groupDevice.group.id)) {
                    intervalsByGroup[groupDevice.group.id].push({ id: groupDeviceId, from: groupDevice.from, to: groupDevice.to });
                }
            });
            Object.keys(intervalsByGroup).forEach(groupId => {
                intervalsByGroup[groupId].sort((a, b) => a.from > b.from ? 1 : -1);
            });

            const groupLogStrings = [`\nSource: ${ source } Device: ${ deviceId }\n----------------`];
            Object.keys(intervalsByGroup).forEach(groupId => {
                const intervals = intervalsByGroup[groupId];
                groupLogStrings.push(`\nGroup ID: ${ groupId }`);
                intervals.forEach(interval => groupLogStrings.push(`\n\tGroupLinkedEntityId: ${ interval.id } From: ${ interval.from } --> To: ${ interval.to }`));
            });
            logger.log(logger.Source.SYNC, groupLogStrings.join(""));
        });
    }

    private pushChangedToServer(localGroupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>) {
        return new Promise((resolve, reject) => {
            let calls: [IModifiedType, ISyncAddSet<IGroupLinkedDevice>][] = [];

            // Prepare local logs for push to server
            Object.keys(localGroupLinkedDevices).forEach(deviceId => {
                Object.keys(localGroupLinkedDevices[deviceId]).forEach(groupDeviceId => {
                    let groupDevice = localGroupLinkedDevices[deviceId][groupDeviceId];
                    if (!groupDevice.id) {
                        throw new Error("Attempting to sync a record without an ID");
                    }

                    if (groupDevice.modified) {
                        let sanitizedLog = extend({}, groupDevice);
                        this.removeClientSideProperties(sanitizedLog);

                        if (groupDevice.modified === "Add" || groupDevice.modified === "Set") {
                            if (groupDevice._syncCounter === undefined) {
                                groupDevice._syncCounter = NumberOfSyncRetries;
                            }
                            if (groupDevice._syncCounter > 0) {
                                groupDevice._syncCounter--;
                                calls.push([groupDevice.modified, { typeName: "GroupDevice", entity: sanitizedLog }]);
                            }
                        }
                    }
                });
            });

            if (calls.length > 0) {
                logger.log(logger.Source.SYNC, common.formatCallForLogger("GroupLinkedDevice", calls), this.api.getUser());
                this.api.multiCall(
                    calls,
                    results => {
                        let getLocalEntity = (entity: IGroupLinkedDevice) => localGroupLinkedDevices[entity.linkedEntity.id][entity.id];
                        this.resolveSyncedEntities<IGroupLinkedDevice>(calls, results, getLocalEntity);
                        this.logGroupsTimeline("local", localGroupLinkedDevices);
                        resolve(localGroupLinkedDevices);
                    },
                    error => {
                        if (isOverLimitException(error) || isNetworkError(error)) {
                            reject(error);
                            return;
                        }

                        this.logGroupsTimeline("local", localGroupLinkedDevices);

                        const requestIndex = error.data.requestIndex;
                        const call = calls[requestIndex];
                        const modified = call && call[0];
                        const callLog = call && call[1].entity;
                        const deviceId = callLog?.linkedEntity.id;
                        const logId = callLog?.id;

                        if (deviceId && logId) {
                            if (isDuplicateException(error) && modified === "Add") {
                                this.resolveAddConflicts(localGroupLinkedDevices)
                                    .then(resolvedGroupDeviceLinks => {
                                        this.setGroupLinkedDevices(resolvedGroupDeviceLinks);
                                        return this.pushChangedToServer(resolvedGroupDeviceLinks);
                                    })
                                    .then(resolve)
                                    .catch(reject);
                            } else if (deviceNotInUserScopeException(error) || isRelationViolatedException(error) || (isDuplicateException(error) && modified === "Set")) {
                                delete localGroupLinkedDevices[deviceId][logId];
                                this.resetInvalidGroupDevice(localGroupLinkedDevices, logId)
                                    .then(() => {
                                        this.store.dispatch(GroupLinkedDeviceActions.setGroupLinkedDevices({ deviceId: deviceId, groupLinkedDevices: localGroupLinkedDevices[deviceId] }));
                                        return this.pushChangedToServer(localGroupLinkedDevices);
                                    })
                                    .then(resolve)
                                    .catch(reject);
                            } else {
                                reject(error);
                            }
                        } else {
                            reject(error);
                        }
                    }
                );
            } else {
                this.logGroupsTimeline("local", localGroupLinkedDevices);
                resolve(localGroupLinkedDevices);
            }
        });
    }

    private resetInvalidGroupDevice(groupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>, invalidEntityId: string) {
        return new Promise((resolve, reject) => {
            if (invalidEntityId) {
                this.api.call("Get", {
                    typeName: "GroupDevice",
                    search: { id: invalidEntityId },
                    resultsLimit: 1
                }, results => {
                    if (results && results.length > 0) {
                        const groupDevice = results[0];
                        // Replace the local version
                        if (!groupLinkedDevices.hasOwnProperty(groupDevice.linkedEntity.id)) {
                            groupLinkedDevices[groupDevice.linkedEntity.id] = {};
                        }
                        groupLinkedDevices[groupDevice.linkedEntity.id][groupDevice.id] = new GroupLinkedDevice(groupDevice);
                    }
                    resolve();
                }, reject);
            } else {
                resolve();
            }
        });
    }

    private resolveAddConflicts(groupLinkedDevices: IIdDictionary<IIdDictionary<GroupLinkedDevice>>): Promise<IIdDictionary<IIdDictionary<GroupLinkedDevice>>> {
        return new Promise((resolve, reject) => {
            let calls: TMultiCallParam[] = [];
            const linksToVerify: GroupLinkedDevice[] = [];

            for (let deviceId of Object.keys(groupLinkedDevices)) {
                let deviceLinks = groupLinkedDevices[deviceId];
                for (let logId of Object.keys(deviceLinks)) {
                    let groupDeviceLog = deviceLinks[logId];
                    if (groupDeviceLog.modified === "Add") {
                        linksToVerify.push(groupDeviceLog);

                        // Simple case - log with id already exists in the database.
                        let duplicateIdCall: TMultiCallParam = ["Get", {
                            typeName: "GroupDevice",
                            search: { id: logId },
                            resultsLimit: 1
                        }];

                        // More complicated case - index violation - log violates unique constraint on Device/Group/ToDate
                        // See "ix_nodevehicle_dtdate_1"
                        let indexViolationCall: TMultiCallParam = ["Get", {
                            typeName: "GroupDevice",
                            search: {
                                deviceSearch: { id: deviceId },
                                fromDate: groupDeviceLog.from,
                                toDate: groupDeviceLog.to
                            }
                        }];
                        calls.push(duplicateIdCall, indexViolationCall);
                    }
                }
            }

            if (calls.length) {
                this.api.multiCall(calls, (results: IGroupLinkedDevice[][])  => {
                    let wasUpdated = false;
                    linksToVerify.forEach((groupDeviceLink, index) => {
                        const [alreadyExistsLinkResults, linkWithSamePropertiesResults] = [results[index * 2], results[index * 2 + 1]];
                        if (alreadyExistsLinkResults?.length > 0) {
                            groupLinkedDevices[groupDeviceLink.linkedEntity.id][groupDeviceLink.id] = new GroupLinkedDevice(alreadyExistsLinkResults[0]);
                            !wasUpdated && (wasUpdated = true);
                        } else if (linkWithSamePropertiesResults?.length > 0) {
                            const linkWithSameProperties = linkWithSamePropertiesResults.find(link => {
                                return groupDeviceLink.to === link.to &&
                                groupDeviceLink.linkedEntity.id === link.linkedEntity.id &&
                                groupDeviceLink.group.id === link.group.id;
                            });
                            if (linkWithSameProperties) {
                                delete groupLinkedDevices[groupDeviceLink.linkedEntity.id][groupDeviceLink.id];
                                groupLinkedDevices[groupDeviceLink.linkedEntity.id][linkWithSameProperties.id] = new GroupLinkedDevice(linkWithSameProperties);
                                !wasUpdated && (wasUpdated = true);
                            }
                        }
                    });

                    if (wasUpdated) {
                        resolve(groupLinkedDevices);
                    } else {
                        // Reject if nothing was changed, we do not want to keep repeatedly trying for no reason
                        reject(new Error("Duplicate exception detected, but no resolution was found."));
                    }
                }, reject);
            } else {
                resolve(groupLinkedDevices);
            }
        });
    }
}